import {
  Anchor,
  Badge,
  Button,
  Group,
  Stack,
  Switch,
  Table,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { IconPlus } from '@tabler/icons-react';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useOrgs, type OrgDto } from '../api/orgs';

export function OrgsListPage() {
  const [includeArchived, setIncludeArchived] = useState(false);
  const [search, setSearch] = useState('');
  const { data, isLoading, isError } = useOrgs({ includeArchived });

  const filtered = useMemo(() => {
    if (!data) return [];
    const q = search.trim().toLowerCase();
    if (!q) return data;
    return data.filter((o) => o.name.toLowerCase().includes(q));
  }, [data, search]);

  const byId = useMemo(() => new Map((data ?? []).map((o) => [o.id, o] as const)), [data]);
  const childCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const o of data ?? []) {
      if (o.parentOrganisationId) {
        counts.set(o.parentOrganisationId, (counts.get(o.parentOrganisationId) ?? 0) + 1);
      }
    }
    return counts;
  }, [data]);

  return (
    <Stack gap="lg">
      <Group justify="space-between" align="flex-end">
        <Title order={2}>Organisations</Title>
        <Button
          component={Link}
          to="/orgs/new"
          leftSection={<IconPlus size={16} />}
          variant="filled"
        >
          New organisation
        </Button>
      </Group>

      <Group justify="space-between">
        <TextInput
          placeholder="Search by name…"
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
          w={320}
        />
        <Switch
          label="Show archived"
          checked={includeArchived}
          onChange={(e) => setIncludeArchived(e.currentTarget.checked)}
        />
      </Group>

      {isLoading && <Text c="dimmed">Loading…</Text>}
      {isError && <Text c="red">Failed to load organisations.</Text>}

      {!isLoading && !isError && data !== undefined && (
        <Table withTableBorder>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Name</Table.Th>
              <Table.Th style={{ width: 100 }}>Region</Table.Th>
              <Table.Th style={{ width: 140 }}>Status</Table.Th>
              <Table.Th style={{ width: 160 }}>Parent</Table.Th>
              <Table.Th style={{ width: 110 }}>Total cases</Table.Th>
              <Table.Th style={{ width: 120 }}>Isolation</Table.Th>
              <Table.Th style={{ width: 180 }}>Created</Table.Th>
              <Table.Th style={{ width: 120 }}>Details</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {filtered.length === 0 ? (
              <Table.Tr>
                <Table.Td colSpan={8}>
                  <Text c="dimmed">No organisations match.</Text>
                </Table.Td>
              </Table.Tr>
            ) : (
              filtered.map((org: OrgDto) => (
                <Table.Tr key={org.id}>
                  <Table.Td>
                    <Group gap="xs" wrap="nowrap">
                      {org.name}
                      {org.synthetic && (
                        <Badge color="grape" variant="light">
                          Synthetic
                        </Badge>
                      )}
                      {(childCounts.get(org.id) ?? 0) > 0 && (
                        <Badge color="blue" variant="light">
                          {childCounts.get(org.id)}{' '}
                          {childCounts.get(org.id) === 1 ? 'site' : 'sites'}
                        </Badge>
                      )}
                    </Group>
                  </Table.Td>
                  <Table.Td>
                    <Badge variant="light">{org.region.toUpperCase()}</Badge>
                  </Table.Td>
                  <Table.Td>
                    {org.deletedAt ? (
                      <Badge color="gray">Archived</Badge>
                    ) : (
                      <Badge color="green">Active</Badge>
                    )}
                  </Table.Td>
                  <Table.Td>
                    {org.parentOrganisationId ? (
                      <Anchor component={Link} to={`/orgs/${org.parentOrganisationId}`}>
                        {byId.get(org.parentOrganisationId)?.name ?? org.parentOrganisationId}
                      </Anchor>
                    ) : (
                      <Text c="dimmed">—</Text>
                    )}
                  </Table.Td>
                  <Table.Td>{org.caseCount ?? 0}</Table.Td>
                  <Table.Td>
                    {org.isolation?.status === 'isolated' ? (
                      <Badge color="orange">Isolated</Badge>
                    ) : null}
                  </Table.Td>
                  <Table.Td>{new Date(org.createdAt).toLocaleDateString('en-GB')}</Table.Td>
                  <Table.Td>
                    <Anchor component={Link} to={`/orgs/${org.id}`}>
                      open
                    </Anchor>
                  </Table.Td>
                </Table.Tr>
              ))
            )}
          </Table.Tbody>
        </Table>
      )}
    </Stack>
  );
}
